import {
    Badge,
    Box,
    Button,
    Checkbox,
    Flex,
    Grid,
    HStack,
    Icon,
    IconButton,
    Spinner,
    Text,
    useToast,
    VStack,
} from '@chakra-ui/react';
import { useContext, useRef } from 'react';
import { FiCopy } from 'react-icons/fi';
import { TbArrowRight, TbCircleCheck, TbCircleX, TbEditCircle, TbPackageImport } from 'react-icons/tb';
import { DeliveryBoardRangeList, DeliveryBoardRow, DeliveryBoardWorkingFilter } from '../hooks/useDeliveryBoardFeed';
import { DeliveryBoardColumn } from '../hooks/useDeliveryBoardData';
import { DeliveryBoardColumnKey } from '../hooks/useDeliveryBoardPreferences';
import SessionContext from '../contexts/SessionContext';
import { isDeliveryRowReceivable } from '../utils/deliveryReceiveAccess';
import { formatPercentage, formatPhoneNumber, formatPrice } from '../functions';

interface DeliveryBoardKanbanProps {
    columns: DeliveryBoardColumn[];
    visibleColumnKeys: DeliveryBoardColumnKey[];
    /** Selection for bulk receive. Card click + checkboxes toggle the blue bar. */
    selectedIds: Set<string>;
    onToggleSelect: (
        row: DeliveryBoardRow,
        rowIndex: number,
        shiftKey: boolean,
        range?: DeliveryBoardRangeList,
    ) => void;
    /** Rows just marked received (kept visible, struck through, undoable). */
    receivedIds: Set<string>;
    onUndoReceive: (row: DeliveryBoardRow) => void;
}

const COLUMN_META: Record<DeliveryBoardWorkingFilter, { title: string; icon: React.ElementType; }> = {
    'active': { title: 'Active', icon: TbCircleCheck as React.ElementType },
    'part-changes': { title: 'ETA Changes', icon: TbEditCircle as React.ElementType },
    'backordered': { title: 'Backordered', icon: TbPackageImport as React.ElementType },
    'unfulfilled': { title: 'Unfulfilled', icon: TbCircleX as React.ElementType },
};

const formatShortDate = (date: Date) => {
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = String(date.getFullYear()).slice(-2);
    return `${month}/${day}/${year}`;
};

const formatTimeOfDay = (date: Date) => date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
});

const formatDateWithTime = (date: Date | null) => {
    if (!date) return '—';
    return `${formatShortDate(date)} • ${formatTimeOfDay(date)}`;
};

const PART_DETAIL_KEYS: DeliveryBoardColumnKey[] = [
    'partNumber',
    'vendorPhone',
    'timeStarted',
    'timePlaced',
    'listPrice',
    'buyPrice',
    'grossMargin',
    'vendorEmail',
];

const RO_DETAIL_KEYS: DeliveryBoardColumnKey[] = [
    'vin',
    'vehicle',
    'customerName',
];

/** Matches Home/Dashboard — default Chakra blue when checked. */
const receiveCheckboxSx = {
    '& .chakra-checkbox__control': {
        width: '14px',
        height: '14px',
    },
    '& .chakra-checkbox__icon': {
        fontSize: '9px',
    },
};

const DeliveryBoardKanban = ({
    columns,
    visibleColumnKeys,
    selectedIds,
    onToggleSelect,
    receivedIds,
    onUndoReceive,
}: DeliveryBoardKanbanProps) => {
    const toast = useToast();
    const session = useContext(SessionContext);
    const shiftKeyRef = useRef(false);
    const visibleSet = new Set(visibleColumnKeys);

    const handleCopy = (value: string, label: string) => (event: React.MouseEvent) => {
        event.stopPropagation();
        navigator.clipboard.writeText(value);
        toast({
            title: `${label} copied`,
            status: 'success',
            duration: 2000,
            isClosable: true,
        });
    };

    const renderCopyButton = (value: string, label: string) => (
        <IconButton
            aria-label={`Copy ${label.toLowerCase()}`}
            icon={<Icon as={FiCopy as React.ElementType} w={2.5} h={2.5} />}
            size="xs"
            variant="ghost"
            color="gray.400"
            _hover={{ color: 'gray.600', bg: 'gray.100' }}
            onClick={handleCopy(value, label)}
            minW="auto"
            h="auto"
            p={0.5}
            ml={0.5}
        />
    );

    const renderInlineSegment = (key: DeliveryBoardColumnKey, row: DeliveryBoardRow): React.ReactNode => {
        switch (key) {
            case 'partNumber':
                return row.partNumber ? (
                    <>
                        <Text as="span">{row.partNumber}</Text>
                        {renderCopyButton(row.partNumber, 'Part number')}
                    </>
                ) : null;
            case 'vendorPhone':
                return row.vendorPhone ? (
                    <>
                        <Text as="span">{formatPhoneNumber(row.vendorPhone)}</Text>
                        {renderCopyButton(row.vendorPhone, 'Phone number')}
                    </>
                ) : null;
            case 'vendorEmail':
                return row.vendorEmail ? (
                    <>
                        <Text as="span">{row.vendorEmail}</Text>
                        {renderCopyButton(row.vendorEmail, 'Email')}
                    </>
                ) : null;
            case 'timeStarted':
                return row.timeStarted ? <Text as="span">Started {formatDateWithTime(row.timeStarted)}</Text> : null;
            case 'timePlaced':
                return row.timePlaced ? <Text as="span">Placed {formatDateWithTime(row.timePlaced)}</Text> : null;
            case 'listPrice':
                return row.listPrice != null ? <Text as="span">List {formatPrice(row.listPrice)}</Text> : null;
            case 'buyPrice':
                return row.buyPrice != null ? <Text as="span">Buy {formatPrice(row.buyPrice)}</Text> : null;
            case 'grossMargin':
                return row.grossMargin != null ? <Text as="span">Margin {formatPercentage(row.grossMargin)}</Text> : null;
            case 'vin':
                return row.vin ? (
                    <>
                        <Text as="span" fontFamily="mono">{row.vin}</Text>
                        {renderCopyButton(row.vin, 'VIN')}
                    </>
                ) : null;
            case 'vehicle':
                return row.vehicle ? <Text as="span">{row.vehicle}</Text> : null;
            case 'customerName':
                return row.customerName ? <Text as="span">{row.customerName}</Text> : null;
            default:
                return null;
        }
    };

    const buildSegments = (
        keys: DeliveryBoardColumnKey[],
        row: DeliveryBoardRow,
    ): { key: DeliveryBoardColumnKey; node: React.ReactNode; }[] => (
        keys
            .filter((key) => visibleSet.has(key))
            .map((key) => ({ key, node: renderInlineSegment(key, row) }))
            .filter((entry) => entry.node !== null)
    );

    const renderDotSegments = (
        segments: { key: DeliveryBoardColumnKey; node: React.ReactNode; }[],
        leading?: React.ReactNode,
    ) => {
        if (segments.length === 0 && !leading) return null;
        return (
            <Flex flexWrap="wrap" rowGap={1} columnGap={0} align="center" fontSize="xs" color="gray.600">
                {leading && (
                    <Box as="span" display="inline-flex" alignItems="center">
                        {leading}
                    </Box>
                )}
                {segments.map((segment, index) => (
                    <Box
                        key={segment.key}
                        as="span"
                        display="inline-flex"
                        alignItems="center"
                    >
                        {(leading || index > 0) && (
                            <Text as="span" color="gray.300" mx={1.5}>·</Text>
                        )}
                        {segment.node}
                    </Box>
                ))}
            </Flex>
        );
    };

    return (
        <Grid
            templateColumns={`repeat(${columns.length}, minmax(0, 1fr))`}
            gap={4}
            px={6}
            pt={2}
            pb={4}
            flex={1}
            minH={0}
            h="100%"
        >
            {columns.map((column) => {
                const meta = COLUMN_META[column.filter];
                // Card indices are column-local, so shift-select ranges carry the
                // column they belong to.
                const columnRange: DeliveryBoardRangeList = {
                    id: `column:${column.filter}`,
                    rows: column.rows,
                };
                return (
                    <Flex
                        key={column.filter}
                        direction="column"
                        borderWidth="1px"
                        borderColor="gray.200"
                        borderRadius="xl"
                        bg="gray.50"
                        overflow="hidden"
                        minH={0}
                        h="100%"
                    >
                        <HStack
                            justify="space-between"
                            px={4}
                            py={3}
                            borderBottomWidth="1px"
                            borderColor="gray.200"
                            bg="white"
                            flexShrink={0}
                        >
                            <HStack spacing={2}>
                                <Icon as={meta.icon} boxSize={4} color="gray.600" />
                                <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                    {meta.title}
                                </Text>
                            </HStack>
                            <Badge
                                bg="gray.500"
                                color="white"
                                borderRadius="full"
                                px={2.5}
                                py={0.5}
                                fontSize="xs"
                                fontWeight="semibold"
                            >
                                {column.rows.length}{column.hasMore ? '+' : ''}
                            </Badge>
                        </HStack>
                        <Box
                            flex={1}
                            minH={0}
                            overflow="auto"
                            sx={{
                                '&::-webkit-scrollbar': { width: '6px' },
                                '&::-webkit-scrollbar-thumb': {
                                    background: '#cbd5e0',
                                    borderRadius: '3px',
                                },
                                scrollbarWidth: 'thin',
                                scrollbarColor: '#cbd5e0 transparent',
                            }}
                        >
                            <VStack spacing={3} align="stretch" p={3}>
                                {column.isLoading ? (
                                    <Flex justify="center" py={6}>
                                        <Spinner size="sm" color="blue.500" />
                                    </Flex>
                                ) : column.isError ? (
                                    <Text fontSize="sm" color="red.500" px={1}>
                                        Failed to load.
                                    </Text>
                                ) : column.rows.length === 0 ? (
                                    <Text fontSize="sm" color="gray.500" px={1}>
                                        No parts in this bucket.
                                    </Text>
                                ) : (
                                    <>
                                        {column.rows.map((row, rowIndex) => {
                                            const isSelected = selectedIds.has(row._id);
                                            const isReceived = receivedIds.has(row._id);
                                            // Just-received cards keep their checkbox for undo.
                                            const receivable = isReceived || isDeliveryRowReceivable(session, row);
                                            return (
                                            <Box
                                                key={row._id}
                                                position="relative"
                                                bg={isSelected ? 'blue.50' : 'white'}
                                                borderWidth="1px"
                                                borderColor={isReceived ? 'blue.200' : isSelected ? 'blue.300' : 'gray.200'}
                                                borderRadius="lg"
                                                p={3}
                                                cursor={receivable && !isReceived ? 'pointer' : 'default'}
                                                opacity={isReceived ? 0.65 : 1}
                                                _hover={{
                                                    borderColor: isReceived ? 'blue.300' : '#0071E3',
                                                    boxShadow: 'none',
                                                    '& .delivery-card-checkbox': {
                                                        opacity: 1,
                                                        pointerEvents: 'auto',
                                                    },
                                                }}
                                                onClick={(e) => {
                                                    if (!receivable || isReceived) return;
                                                    onToggleSelect(row, rowIndex, e.shiftKey, columnRange);
                                                }}
                                            >
                                                {receivable && (
                                                    <Box
                                                        className="delivery-card-checkbox"
                                                        position="absolute"
                                                        top={2}
                                                        right={2}
                                                        opacity={isSelected || isReceived ? 1 : 0}
                                                        pointerEvents={isSelected || isReceived ? 'auto' : 'none'}
                                                        transition="opacity 150ms ease"
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <Checkbox
                                                            aria-label={isReceived ? 'Undo received' : 'Select part'}
                                                            isChecked={isSelected || isReceived}
                                                            size="sm"
                                                            sx={receiveCheckboxSx}
                                                            onMouseDown={(e) => {
                                                                e.stopPropagation();
                                                                shiftKeyRef.current = e.shiftKey;
                                                            }}
                                                            onChange={() => (
                                                                isReceived
                                                                    ? onUndoReceive(row)
                                                                    : onToggleSelect(row, rowIndex, shiftKeyRef.current, columnRange)
                                                            )}
                                                        />
                                                    </Box>
                                                )}
                                                {isReceived && (
                                                    <Badge colorScheme="green" borderRadius="full" fontSize="10px" mb={1}>
                                                        Received
                                                    </Badge>
                                                )}
                                                {row.state === 'unfulfilled' && row.action && (
                                                    <Badge
                                                        colorScheme={row.action === 'cancellation' ? 'red' : 'orange'}
                                                        borderRadius="full"
                                                        fontSize="10px"
                                                        mb={1}
                                                    >
                                                        {row.action === 'cancellation' ? 'Cancelled' : 'Returned'}
                                                    </Badge>
                                                )}
                                                {visibleSet.has('vendorName') && (
                                                    <Text fontSize="xs" color="gray.500" mb={1} pr={receivable ? 6 : 0}>
                                                        {row.vendorName ?? 'Unknown Vendor'}
                                                    </Text>
                                                )}
                                                {visibleSet.has('description') && (
                                                    <Text
                                                        fontSize="sm"
                                                        fontWeight="semibold"
                                                        color="gray.800"
                                                        noOfLines={2}
                                                        textDecoration={isReceived ? 'line-through' : undefined}
                                                    >
                                                        {row.description}
                                                    </Text>
                                                )}
                                                {(() => {
                                                    const partSegments = buildSegments(PART_DETAIL_KEYS, row);
                                                    if (partSegments.length === 0) return null;
                                                    return (
                                                        <Box mt={2}>
                                                            {renderDotSegments(partSegments)}
                                                        </Box>
                                                    );
                                                })()}
                                                {(() => {
                                                    const roSegments = buildSegments(RO_DETAIL_KEYS, row);
                                                    const showRoNumber = visibleSet.has('roNumber');
                                                    const showEtaBadge = visibleSet.has('eta') && !!row.eta.date;
                                                    if (!showRoNumber && roSegments.length === 0 && !showEtaBadge) return null;
                                                    const leading = showRoNumber ? (
                                                        <Text as="span" fontWeight="semibold" color="gray.700">
                                                            RO {row.roNumber}
                                                        </Text>
                                                    ) : null;
                                                    return (
                                                        <Flex
                                                            mt={3}
                                                            pt={2}
                                                            borderTopWidth="1px"
                                                            borderColor="gray.100"
                                                            align="flex-start"
                                                            justify="space-between"
                                                            gap={2}
                                                        >
                                                            <Box minW={0} flex={1}>
                                                                {renderDotSegments(roSegments, leading)}
                                                            </Box>
                                                            {showEtaBadge && (
                                                                row.originalEta ? (
                                                                    <Badge
                                                                        colorScheme="orange"
                                                                        borderRadius="full"
                                                                        flexShrink={0}
                                                                        display="inline-flex"
                                                                        alignItems="center"
                                                                        gap={1}
                                                                    >
                                                                        <Text as="span" textDecoration="line-through" opacity={0.7}>
                                                                            {row.originalEta.label}
                                                                        </Text>
                                                                        <Icon as={TbArrowRight as React.ElementType} boxSize={3} />
                                                                        <Text as="span">{row.eta.label}</Text>
                                                                    </Badge>
                                                                ) : (
                                                                    <Badge colorScheme="purple" borderRadius="full" flexShrink={0}>
                                                                        {row.eta.label}
                                                                    </Badge>
                                                                )
                                                            )}
                                                        </Flex>
                                                    );
                                                })()}
                                            </Box>
                                            );
                                        })}
                                        {column.hasMore && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={column.loadMore}
                                                isLoading={column.isFetching}
                                                borderColor="gray.300"
                                                color="gray.700"
                                                _hover={{ bg: 'gray.100' }}
                                                w="100%"
                                            >
                                                Load more
                                            </Button>
                                        )}
                                    </>
                                )}
                            </VStack>
                        </Box>
                    </Flex>
                );
            })}
        </Grid>
    );
};

export default DeliveryBoardKanban;
